import React, { useEffect, useState } from 'react';
import { BriefcaseIcon, ChevronsDown, ChevronsUp, ClockIcon, DownloadIcon, Loader2 } from 'lucide-react';
import httpService, { ApiError } from '../../services/httpService';
import { useAuth } from '../../context/AuthContext';
import { ErrorIcon } from '../../icons';
import HistoryTimeline from './ApplicationHistoryTimeline';
import StatusBadge from '../OtherPage/StatusBadge';
import { useParams } from 'react-router-dom';
import { 
    ApplicationListResponse, 
    JobApplication, 
    RawApplication 
} from '../../types/application';
import { mapRawToJobApplication } from '../../utils/mappers';
import { isNoDataError } from '../../utils/errorUtils';

const PRIMARY_TEXT_CLASS = "text-indigo-600 dark:text-indigo-400";
const CARD_BG_CLASS = "bg-white dark:bg-gray-800";
const BORDER_CLASS = "border-gray-200 dark:border-gray-700";

// --- MAIN COMPONENT ---
const ApplicantJobApplication: React.FC = () => {
    const [applications, setApplications] = useState<JobApplication[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const { id } = useParams(); // assuming route like /applications/:id
    const { fetchUser } = useAuth();

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);


    // --- Fetch applications ---
    useEffect(() => {
        const fetchApplications = async () => {
            try {
                const response = await httpService.get<ApplicationListResponse>("/job-apply/my-applications");
                const formatted = (response?.data || []).map(mapRawToJobApplication);

                setApplications(formatted);

                // 👇 NEW LOGIC
                if (id) {
                    const found = formatted.find(app => app.id === id);
                    if (found) {
                        setExpandedId(id);
                    } else {
                        setExpandedId(formatted[0]?.id ?? null);
                    }
                } else {
                    setExpandedId(formatted[0]?.id ?? null);
                }

            } catch (err: unknown) {
                const apiErr = err as ApiError;
                setError(apiErr.data?.message || "Failed to fetch applications.");
            } finally {
                setLoading(false);
            }
        };

        fetchApplications();
    }, [id]);

    // --- PDF Download with Loader ---
    const handleDownloadPDF = async (applicationId: string) => {
        try {
            setDownloading(true);
            const response = await httpService.get(`/job-apply/${applicationId}/download`, {
                responseType: "blob",
            });

            const blob = new Blob([response as Blob], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `application_${applicationId}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Download failed:", err);
            alert("Failed to download PDF. Please try again.");
        } finally {
            setDownloading(false);
        }
    };

    // --- UI States ---
    if (loading)
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
                <p className="text-lg text-indigo-600 dark:text-indigo-400">Loading your applications...</p>
            </div>
        );

    if (error)
        return (
            <div className=" flex items-center justify-center dark:bg-gray-900 p-6">
                <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-xl rounded-xl p-8 text-center border border-gray-200 dark:border-gray-700">

                    {/* Icon */}
                    <div className="flex justify-center mb-4">
                        <ErrorIcon className="h-12 w-12 text-red-500" />
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        Something went wrong
                    </h2>

                    {/* Description */}
                    <p className="mt-3 text-gray-600 dark:text-gray-400">
                        We couldn't load your applications right now.
                        Please try again or check your internet connection.
                    </p>

                    {/* Technical message (optional small text) */}
                    <p className="mt-2 text-xs text-gray-400">{error}</p>

                    {/* Actions */}
                    <div className="mt-6 flex justify-center gap-3">
                        <button
                            onClick={() => window.location.reload()}
                            className="px-6 py-1 brand-bg brand-hover-bg text-white rounded-lg shadow"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );

    if (applications.length === 0)
        return (
            <div className="min-h-screen p-8 bg-gray-100 dark:bg-gray-900">
                <div className={`p-10 text-center ${CARD_BG_CLASS} rounded-lg shadow-lg`}>
                    <BriefcaseIcon className={`mx-auto h-12 w-12 ${PRIMARY_TEXT_CLASS}`} />
                    <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">No Applications Found</h2>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">
                        It looks like you haven't applied for any jobs yet.
                    </p>
                </div>
            </div>
        );

    return (
        <div className="min-h-screen p-4 dark:bg-gray-900 relative">
            <header className="mb-4">
                <h1 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white">👋 Your Job Applications</h1>
                <p className="mt-2 text-xs lg:text-sm text-gray-600 dark:text-gray-400">
                    Track the status and download your application PDFs.
                </p>
            </header>

            <div>
                {applications.map((app) => {
                    const isExpanded = expandedId === app.id;

                    return (
                        <div key={app.id} className="shadow-md rounded-lg p-6 space-y-1 mb-4 border border-gray-100">

                            <div className="flex items-center justify-between flex-wrap">
                                <h2 className="font-bold">
                                    {app.jobTitle}
                                </h2>

                                <p className="text-xs font-medium brand-text">
                                    Last Update: {new Date(app.lastUpdated).toLocaleString('en-IN', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true
                                    })}
                                </p>
                            </div>

                            <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Applied On: {new Date(app.applicationDate).toLocaleString('en-IN', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true
                                    })}
                                </p>
                            </div>

                            <div className="flex justify-between gap-4 pb-2 flex-wrap">
                                <div className='flex items-center gap-2'>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                        Current Status:
                                    </p>
                                    <StatusBadge
                                        status={app.currentStatus}
                                        colorCode={app.colorCode}
                                    />
                                </div>

                                <div className='flex gap-4'>
                                    <button
                                        onClick={() => handleDownloadPDF(app.id)}
                                        disabled={downloading}
                                        className="flex items-center px-4 py-2 text-xs font-medium border brand-border brand-text hover:bg-green-50 rounded-md shadow transition"
                                    >
                                        {downloading ? (
                                            <>
                                                <Loader2 className="animate-spin w-4 h-4 mr-2" />
                                                Downloading...
                                            </>
                                        ) : (
                                            <>
                                                <DownloadIcon className="w-4 h-4 mr-2" />
                                                Download PDF
                                            </>
                                        )}
                                    </button>

                                    <button
                                        onClick={() =>
                                            setExpandedId(prev => prev === app.id ? null : app.id)
                                        }
                                        className="flex items-center px-4 py-2 text-xs font-medium border brand-border brand-text hover:bg-green-50 rounded-md shadow transition"
                                    >
                                        {isExpanded ? (
                                            <>
                                                <ChevronsUp className="w-4 h-4 mr-2" />
                                                Hide Details
                                            </>
                                        ) : (
                                            <>
                                                <ChevronsDown className="w-4 h-4 mr-2" />
                                                View Details
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="mt-6 border-t pt-2">
                                    <h3 className="text-sm font-semibold mb-4 flex items-center">
                                        <ClockIcon className="h-4 w-4 mr-2" />
                                        Application Status History
                                    </h3>

                                    <HistoryTimeline history={app.statusHistory} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Full Screen Download Loader */}
            {downloading && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
                        <Loader2 className="animate-spin text-indigo-600 w-10 h-10 mb-3" />
                        <p className="text-gray-700 dark:text-gray-200 text-lg font-medium">
                            Downloading PDF...
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApplicantJobApplication;
